import { styled } from '@linaria/react';
import { Loader, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  useGoalTeamFocusChartData,
  useOutcomeTeamFocusChartData,
} from '../../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { secondaryBase, skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip as InfoTooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { CHART_COLORS } from './color-helpers';

type TeamFocusChartProps = {
  outcomeId?: string | undefined;
  goalId?: string | undefined;
};

export function TeamFocusChart({ outcomeId, goalId }: TeamFocusChartProps) {
  const i18n = useTranslation();
  // Fetch aggregated team focus chart data
  const { data: outcomeChartData, query: outcomeQuery } = useOutcomeTeamFocusChartData(outcomeId, 6);
  const { data: goalChartData, query: goalQuery } = useGoalTeamFocusChartData(goalId, 6);

  const chartDataResponse = outcomeId ? outcomeChartData : goalChartData;
  const query = outcomeId ? outcomeQuery : goalQuery;

  const { chartData, teamNames } = useMemo(() => {
    if (!chartDataResponse || !chartDataResponse.months || !chartDataResponse.teams) {
      return { chartData: [], teamNames: new Map<string, string>() };
    }

    const teamNamesMap = new Map<string, string>();

    // Build team names map
    chartDataResponse.teams.forEach((team: any) => {
      teamNamesMap.set(team.id, team.name);
    });

    // Transform API response into chart format
    const data = chartDataResponse.months.map((month: string) => {
      const dataPoint: Record<string, string | number> = { month };

      chartDataResponse.teams.forEach((team: any) => {
        const focusValue = team.data[month] || 0;
        dataPoint[team.id] = focusValue;
      });

      return dataPoint;
    });

    return { chartData: data, teamNames: teamNamesMap };
  }, [chartDataResponse]);

  const teams = chartDataResponse?.teams || [];

  if (!outcomeId && !goalId) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="people_alt" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.team_focus_over_time')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  if (query.isLoading) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="people_alt" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.team_focus_over_time')}</SectionTitle>
            <Loader size="sm" variant="dots" color={secondaryBase} />
          </SectionHeader>
          <ChartContainer />
        </Stack>
      </SectionCard>
    );
  }

  if (chartData.length === 0) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="people_alt" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.team_focus_over_time')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  const teamIds = Array.from(teamNames.keys());

  return (
    <SectionCard>
      <Stack gap={12}>
        <SectionHeader>
          <Icon name="people_alt" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
          <SectionTitle>{i18n.t('strategy.overview.sections.team_focus_over_time')}</SectionTitle>
          <InfoTooltip variant="black" label={i18n.t('strategy.overview.tooltips.team_focus_over_time')} zIndex={1000}>
            <Icon name="info" size={16} color={skyDark} style={{ cursor: 'help' }} />
          </InfoTooltip>
        </SectionHeader>

        <ChartContainer>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
              <XAxis
                dataKey="month"
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.month'),
                  position: 'insideBottom',
                  offset: -15,
                  style: { fontSize: '12px', fill: skyDark },
                }}
                height={70}
              />
              <YAxis
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.focus_percentage'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: '12px', fill: skyDark },
                }}
                domain={[0, 100]}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  return (
                    <div
                      style={{
                        backgroundColor: 'white',
                        border: `1px solid ${skyLight}`,
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '12px',
                        fontFamily: 'Figtree',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{payload[0].payload.month}</div>
                      {payload.map((entry: any, index: number) => {
                        const team = teams.find((t: any) => t.id === entry.dataKey);
                        const isOthers = team?.id === 'others';

                        return (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: entry.color,
                                  borderRadius: '2px',
                                }}
                              />
                              <span style={{ fontWeight: 'medium' }}>{team?.name || 'Unknown'}:</span>
                              <span style={{ fontWeight: 'bold' }}>{entry.value}%</span>
                            </div>
                            {isOthers && team.included_initiatives && team.included_initiatives.length > 0 && (
                              <div style={{ marginLeft: '20px', marginTop: '4px', fontSize: '11px', color: skyDark }}>
                                <div style={{ fontStyle: 'italic', marginBottom: '2px' }}>
                                  Aggregated from initiatives:
                                </div>
                                {team.included_initiatives.map((inc: any) => (
                                  <div key={inc.id}>• {inc.name}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', fontFamily: 'Figtree', paddingTop: '20px' }}
                formatter={(value: string) => teamNames.get(value) || value}
              />
              {teamIds.map((teamId, index) => (
                <Line
                  key={teamId}
                  type="monotone"
                  dataKey={teamId}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </Stack>
    </SectionCard>
  );
}

const SectionCard = styled.div`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${skyLight};
  flex: 1;
  min-height: 350px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;

const SectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  text-align: left;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px dashed ${skyLight};
`;
